import React from 'react';
import { Mission, MissionStatus } from '../../../types/mission';
import styles from './MissionCard.module.css';

/**
 * Props for the MissionCard component
 */
interface MissionCardProps {
  mission: Mission;
  status: MissionStatus;
  onClick: () => void;
}

/**
 * MissionCard component
 * 
 * Displays a mission as a selectable card with information about the mission
 * and visual indicators for its status (locked, available, or completed).
 */
const MissionCard: React.FC<MissionCardProps> = ({ mission, status, onClick }) => {
  // Generate status-specific class names
  const cardClassNames = `${styles.card} ${styles[status]}`;
  
  // Define status-specific labels
  const statusLabels = {
    locked: 'Classified',
    available: 'Available',
    completed: 'Completed',
  };
  
  // Get the mission type label with capitalized first letter
  const missionTypeLabel = mission.type.charAt(0).toUpperCase() + mission.type.slice(1);
  
  // Calculate difficulty display (stars)
  const renderDifficultyStars = () => {
    return (
      <div className={styles.stars}>
        {[...Array(3)].map((_, i) => (
          <span 
            key={i} 
            className={i < mission.difficulty ? styles.starFilled : styles.starEmpty}
            aria-hidden="true"
          >
            ★
          </span>
        ))}
        <span className={styles.srOnly}>
          Difficulty level {mission.difficulty} out of 3
        </span>
      </div>
    );
  };

  return (
    <div 
      className={cardClassNames}
      onClick={status !== 'locked' ? onClick : undefined}
      role={status !== 'locked' ? 'button' : 'article'}
      tabIndex={status !== 'locked' ? 0 : undefined}
      aria-disabled={status === 'locked'}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && status !== 'locked') {
          onClick();
          e.preventDefault();
        }
      }}
    >
      <div className={styles.header}>
        <div className={styles.statusBadge} data-status={status}>
          {statusLabels[status]}
          {/* @TODO: Add asset: Status icons (lock for locked, check for completed) */}
        </div>
        <h3 className={styles.title}>
          {status === 'locked' ? 'Classified Operation' : mission.title}
        </h3>
      </div>
      
      <div className={styles.content}>
        {status === 'locked' ? (
          <p className={styles.lockedMessage}>
            This mission requires clearance.
            Complete previous missions to unlock.
          </p>
        ) : (
          <>
            <p className={styles.description}>
              {mission.description}
            </p>
            
            <div className={styles.meta}>
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Type:</span>
                <span className={styles.metaValue}>{missionTypeLabel}</span>
              </div>
              
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Stage:</span>
                <span className={styles.metaValue}>{mission.stage}</span>
              </div>
              
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Difficulty:</span>
                {renderDifficultyStars()}
              </div>
              
              {mission.timeLimit && (
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Time Limit:</span>
                  <span className={styles.metaValue}>{mission.timeLimit}s</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      <div className={styles.footer}>
        {status === 'locked' ? (
          <div className={styles.lockedFooter}>
            {/* @TODO: Add asset: Lock icon */}
            🔒 Requires security clearance
          </div>
        ) : status === 'completed' ? (
          <div className={styles.completedFooter}>
            {/* @TODO: Add asset: Checkmark icon */}
            ✓ Mission completed - Replay available
          </div>
        ) : (
          <div className={styles.availableFooter}>
            Begin mission →
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionCard; 